// assets/announcement-carousel.js

// Small, robust effect carousel + marquee and button builders

class EffectCarousel extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.children || []);
    this.index = 0;
    this.autoplayDelay = parseInt(this.getAttribute('autoplay')) || 0;

    // initialize slide states & inline fade transitions so it works without extra CSS
    this.slides.forEach((slide, i) => {
      slide.style.transition = slide.style.transition || 'opacity .45s ease';
      slide.style.opacity = i === this.index ? '1' : '0';
      slide.classList.toggle('is-selected', i === this.index);
      if (i !== this.index) slide.classList.add('reveal-invisible');
    });

    if (this.autoplayDelay > 0) {
      this.startAutoplay();
    }

    // pause on hover (nice UX)
    this.addEventListener('mouseenter', () => this.stopAutoplay());
    this.addEventListener('mouseleave', () => {
      if (this.autoplayDelay > 0) this.startAutoplay();
    });
  }

  startAutoplay() {
    this.stopAutoplay();
    this.timer = setInterval(() => this.next(), this.autoplayDelay);
  }

  stopAutoplay() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  next() {
    this.goto(this.index + 1);
  }

  prev() {
    this.goto(this.index - 1);
  }

  goto(newIndex) {
    if (!this.slides.length) return;
    const fromSlide = this.slides[this.index];
    this.index = (newIndex + this.slides.length) % this.slides.length;
    const toSlide = this.slides[this.index];
    this._transitionTo(fromSlide, toSlide);
  }

  _transitionTo(fromSlide, toSlide) {
    if (fromSlide) {
      fromSlide.classList.remove('is-selected');
      fromSlide.classList.add('reveal-invisible');
      fromSlide.style.opacity = 0;
    }
    if (toSlide) {
      toSlide.classList.remove('reveal-invisible');
      toSlide.classList.add('is-selected');
      // force repaint then set opacity to 1 for smoothness
      requestAnimationFrame(() => {
        toSlide.style.opacity = 1;
      });
    }
  }
}

// Register the carousel element if not present
if (!window.customElements.get('announcement-bar')) {
  class AnnouncementBar extends EffectCarousel {}
  window.customElements.define('announcement-bar', AnnouncementBar);
}

// Built-in extended buttons so markup like <button is="prev-button"> works
if (!window.customElements.get('prev-button')) {
  class PrevButton extends HTMLButtonElement {
    connectedCallback() {
      this.addEventListener('click', () => {
        const targetId = this.getAttribute('aria-controls');
        const target = document.getElementById(targetId);
        if (target && typeof target.prev === 'function') {
          target.prev();
        } else if (target && target.tagName.toLowerCase() === 'announcement-bar') {
          // fallback
          target.prev();
        }
      });
    }
  }
  customElements.define('prev-button', PrevButton, { extends: 'button' });
}

if (!window.customElements.get('next-button')) {
  class NextButton extends HTMLButtonElement {
    connectedCallback() {
      this.addEventListener('click', () => {
        const targetId = this.getAttribute('aria-controls');
        const target = document.getElementById(targetId);
        if (target && typeof target.next === 'function') {
          target.next();
        } else if (target && target.tagName.toLowerCase() === 'announcement-bar') {
          target.next();
        }
      });
    }
  }
  customElements.define('next-button', NextButton, { extends: 'button' });
}


// Marquee / auto-scrolling text element
if (!window.customElements.get('marquee-text')) {
  class MarqueeText extends HTMLElement {
    connectedCallback() {
      // respect reduced-motion preference
      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        return;
      }

      const speedSeconds = parseInt(this.getAttribute('scrolling-speed')) || 40;
      this.style.setProperty('--marquee-speed', `${speedSeconds}s`);

      // duplicate content so the animation can loop seamlessly
      const content = this.innerHTML;
      this.innerHTML = ''; 
      this.innerHTML = `<div class="marquee-track">${content}${content}</div>`;
      this.classList.add('ready');

      // pause on hover
      this.addEventListener('mouseenter', () => {
        const track = this.querySelector('.marquee-track');
        if (track) track.style.animationPlayState = 'paused';
      });
      this.addEventListener('mouseleave', () => {
        const track = this.querySelector('.marquee-track');
        if (track) track.style.animationPlayState = 'running';
      });
    }
  }
  window.customElements.define('marquee-text', MarqueeText);
}
